import React, { useEffect, useRef, useState } from 'react';
import { imUrl } from '../services/api';

// 浏览器自带的语音识别（Chrome/Edge/Safari 支持），不用后端也不花钱
const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

// 自己发的消息会先乐观上屏（_id 是 tmp+时间戳），服务器存完又通过 ws 广播回来一条真的。
// 只按 _id 去重认不出这是同一条，会变成点一次冒两个气泡，等 agent 回完整体刷新才变回一条。
// 所以先按 _id 认，认不出再按「同一个人、同一句话的 tmp 气泡」认领，替换掉而不是再加一条。
export function mergeIncoming(list, m) {
  if (!m) return list;
  if (list.some((x) => String(x._id) === String(m._id))) return list;
  const i = list.findIndex((x) => String(x._id).startsWith('tmp') && x.sender === m.sender && x.text === m.text);
  if (i < 0) return [...list, m];
  const next = list.slice();
  next[i] = m;
  return next;
}

// 三端共用的聊天框。输入区常驻底部，带语音和快捷短语 —— 聊天是主体，别让人打字打到烦。
export default function ChatBox({
  conversationId, messages, onSend, sending, height = 420, placeholder = '说点什么…', onIncoming, imAuth,
  renderAfter,          // 挂在消息流末尾的卡片
  quickReplies = [],    // 快捷短语，点一下直接发
  showMeta = false,     // agent 的工具执行记录：只给运营看，别漏给商家和博主
  onUploadImage,        // 传了就出现「发图」按钮，用来交发帖截图
  // 运营台专用：这一框能有好几种「发给谁」。传了就在输入框上方出现一排明确的选项，
  // 发送按钮上直接写着这次发给谁 —— 运营真在这儿把内部批注当成回复发过，
  // 靠一行灰色 placeholder 是撑不住这个区别的。
  // 形状：[{ key, label, hint, danger }]，onSend(text, key)
  sendModes,
}) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState((sendModes && sendModes[0]?.key) || '');
  const [listening, setListening] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const taRef = useRef(null);
  const recRef = useRef(null);
  const incomingRef = useRef(onIncoming);
  incomingRef.current = onIncoming;

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const authRef = useRef(imAuth);
  authRef.current = imAuth;

  useEffect(() => {
    if (!conversationId) return undefined;
    let ws;
    try { ws = new WebSocket(imUrl()); } catch { return undefined; }
    ws.onopen = () => ws.send(JSON.stringify({
      type: 'join', conversationId,
      role: authRef.current?.role || '', token: authRef.current?.token || '',
    }));
    ws.onmessage = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.type === 'message' && String(d.conversationId) === String(conversationId)) {
          incomingRef.current && incomingRef.current(d.message);
        }
      } catch { /* 忽略坏包 */ }
    };
    return () => { try { ws.close(); } catch { /* already closed */ } };
  }, [conversationId]);

  // 输入框跟着内容长。上限原来是 120px（约 4 行）——
  // 博主把整篇稿子贴进来审的时候只看得到最后几行，像被截断了，
  // 运营反馈里说的「聊天有字数限制」其实就是这个（并没有字数限制）。
  // 放到屏幕四成高：贴长文能看见大半，又不会把聊天记录整个顶掉。
  const grow = (el) => {
    if (!el) return;
    const cap = Math.max(160, Math.round(window.innerHeight * 0.4));
    el.style.height = '40px';
    el.style.height = `${Math.min(cap, Math.max(40, el.scrollHeight))}px`;
  };

  const send = (val) => {
    const t = (val !== undefined ? val : text).trim();
    if (!t || sending) return;
    setText('');
    if (taRef.current) taRef.current.style.height = '40px';
    onSend(t, mode);
  };

  // 语音：说完自动填进输入框，再点发送（不直接发出去，免得识别错了收不回）
  const toggleVoice = () => {
    if (!SR) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SR();
    rec.lang = 'zh-CN';
    rec.interimResults = true;
    rec.continuous = false;
    let final = '';
    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      setText((prev) => {
        const base = prev.replace(/​.*$/, '');
        return final ? base + final : `${base}​${interim}`;
      });
      setTimeout(() => grow(taRef.current), 0);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => {
      setListening(false);
      setText((prev) => prev.replace(/​/g, ''));
    };
    recRef.current = rec;
    setListening(true);
    rec.start();
  };

  const [copied, setCopied] = useState(-1);
  const copyText = async (text, idx) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); }
    catch {
      // http 或老浏览器下 clipboard 不可用，退回老办法
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(idx);
    setTimeout(() => setCopied(-1), 1500);
  };

  return (
    <div style={s.wrap}>
      <div style={{ ...s.list, height }}>
        {(messages || []).map((m, i) => {
          const mineSide = m.sender === 'agent' || m.sender === 'ops';
          if (m.sender === 'system') return <div key={m._id || i} style={s.sys}>{m.text}</div>;
          return (
            <div key={m._id || i} style={{ ...s.row, justifyContent: mineSide ? 'flex-start' : 'flex-end' }}>
              <div style={{ ...s.bubble, ...(mineSide ? s.agentBubble : s.userBubble) }}>
                {m.sender === 'ops' && <div style={s.opsTag}>运营</div>}
                {/* 消息要能选中复制。运营反馈过：一段长信息（「今晚8点2个人要提前
                    预约停车位不能吃坚果」）要重复发给助理，不让复制就得重打一遍。
                    点一下整条复制是给手机用的 —— 手机上长按选字很难选准 */}
                <div
                  style={s.text}
                  onClick={() => copyText(m.text, i)}
                  title="点一下复制这条"
                >{m.text}</div>
                {copied === i && <div style={s.copied}>已复制</div>}
                {(m.images || []).length > 0 && (
                  <div style={s.imgRow}>
                    {m.images.map((u, k) => <img key={k} src={u} alt="" style={s.shot} />)}
                  </div>
                )}
                {showMeta && m.meta?.applied?.length > 0 && <div style={s.applied}>{m.meta.applied.join('；')}</div>}
              </div>
            </div>
          );
        })}
        {renderAfter}
        {sending && <div style={s.typing}>助理正在输入…</div>}
        <div ref={endRef} />
      </div>

      {quickReplies.length > 0 && (
        <div style={s.quickRow}>
          {quickReplies.map((q) => (
            <span key={q} style={s.quick} onClick={() => send(q)}>{q}</span>
          ))}
        </div>
      )}

      {sendModes && sendModes.length > 1 && (
        <div style={s.modeRow}>
          {/* 连体的分段开关，不是两个按钮。两个独立按钮看着像「两个动作，都能点」，
              而这其实是一个二选一的挡位 —— 运营就是因为分不清才把批注当成回复发的 */}
          <div style={s.seg}>
            {sendModes.map((m, i) => (
              <button
                key={m.key}
                style={{
                  ...s.segItem,
                  ...(i ? s.segItemSep : null),
                  ...(m.key === mode ? (m.danger ? s.segOnDanger : s.segOn) : null),
                }}
                onClick={() => setMode(m.key)}
                title={m.hint || ''}
              >{m.label}</button>
            ))}
          </div>
          <span style={s.modeHint}>{sendModes.find((m) => m.key === mode)?.hint || ''}</span>
        </div>
      )}

      <div style={s.inputRow}>
        {onUploadImage && (
          <>
            <button style={s.mic} disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? '…' : '发图'}
            </button>
            <input
              ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = '';
                if (!files.length) return;
                setUploading(true);
                try { await onUploadImage(files); } finally { setUploading(false); }
              }}
            />
          </>
        )}
        {SR && (
          <button
            style={listening ? s.micOn : s.mic}
            onClick={toggleVoice}
            title={listening ? '停止' : '语音输入'}
          >
            {listening ? '● 录音中' : '语音'}
          </button>
        )}
        <textarea
          ref={taRef}
          style={s.input}
          value={text.replace(/​/g, '')}
          rows={1}
          placeholder={listening ? '说话中…' : placeholder}
          onChange={(e) => { setText(e.target.value); grow(e.target); }}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button style={sending ? s.sendBusy : s.send} disabled={sending} onClick={() => send()}>
          {sending ? '…' : ((sendModes || []).find((m) => m.key === mode)?.label || '发送')}
        </button>
      </div>
    </div>
  );
}

const s = {
  // 「发给谁」是一个二选一的**挡位**，所以画成连体的分段开关，不是两个并排的按钮 ——
  // 两个独立按钮看着像「两个都能点的动作」，而这里一次只能处在一挡上。
  modeRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px 0', flexWrap: 'wrap' },
  seg: { display: 'inline-flex', border: '1px solid #d4d4d8', borderRadius: 8, overflow: 'hidden', background: '#f4f4f5' },
  segItem: { fontSize: 12, lineHeight: '18px', padding: '5px 12px', border: 'none', background: 'transparent', color: '#52525b', cursor: 'pointer', whiteSpace: 'nowrap' },
  segItemSep: { borderLeft: '1px solid #d4d4d8' },
  segOn: { background: '#FA1C51', color: '#fff', fontWeight: 600 },
  segOnDanger: { background: '#52525b', color: '#fff', fontWeight: 600 },
  modeHint: { fontSize: 12, color: '#71717a' },
  wrap: { display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 10, overflow: 'hidden' },
  list: { overflowY: 'auto', padding: 12, background: '#f7f7f8' },
  row: { display: 'flex', marginBottom: 8 },
  bubble: { maxWidth: '78%', padding: '8px 11px', borderRadius: 10, fontSize: 14, lineHeight: 1.6, userSelect: 'text', WebkitUserSelect: 'text' },
  copied: { fontSize: 11, color: '#9ca3af', marginTop: 4 },
  agentBubble: { background: '#fff', border: '1px solid #eee', color: '#111' },
  userBubble: { background: '#FA1C51', color: '#fff' },
  opsTag: { fontSize: 10, color: '#FA1C51', marginBottom: 2, fontWeight: 600 },
  text: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  applied: { marginTop: 6, paddingTop: 6, borderTop: '1px dashed #e5e7eb', fontSize: 11, color: '#16a34a' },
  sys: { textAlign: 'center', fontSize: 11, color: '#9ca3af', margin: '8px 0' },
  imgRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 },
  shot: { width: 92, height: 92, objectFit: 'cover', borderRadius: 6 },
  typing: { fontSize: 11, color: '#9ca3af', padding: '2px 4px' },

  quickRow: {
    display: 'flex', gap: 6, padding: '8px 10px 0', overflowX: 'auto',
    borderTop: '1px solid #f5f5f5', background: '#fff',
  },
  quick: {
    flexShrink: 0, fontSize: 12, color: '#FA1C51', background: '#fff1f4',
    border: '1px solid #ffe0e6', borderRadius: 14, padding: '5px 11px',
    whiteSpace: 'nowrap', cursor: 'pointer',
  },

  // 三个元素等高居中：语音 / 输入框 / 发送
  inputRow: {
    display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #f0f0f0',
    alignItems: 'center', background: '#fff',
  },
  input: {
    flex: 1, border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 12px',
    fontSize: 14, lineHeight: '20px', outline: 'none', resize: 'none',
    fontFamily: 'inherit', boxSizing: 'border-box', height: 40, maxHeight: 120, overflowY: 'auto',
  },
  mic: {
    height: 40, padding: '0 12px', flexShrink: 0, background: '#fff', color: '#6b7280',
    border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 13, cursor: 'pointer',
  },
  micOn: {
    height: 40, padding: '0 12px', flexShrink: 0, background: '#FA1C51', color: '#fff',
    border: '1.5px solid #FA1C51', borderRadius: 10, fontSize: 13, cursor: 'pointer',
  },
  send: {
    height: 40, padding: '0 18px', flexShrink: 0, background: '#FA1C51', color: '#fff',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  sendBusy: {
    height: 40, padding: '0 18px', flexShrink: 0, background: '#fca5a5', color: '#fff',
    border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'default',
  },
};
